<script>
  import CloseIcon from '@/assets/images/svg/icons/cancel-circle.svg';
  import YellowButton from '@/components/base/YellowButton.vue';

  export default {
    name: 'AlertDialog',

    components: {
      CloseIcon,
      YellowButton,
    },

    data: () => ({
      title: '',
      subTitle: '',
      text: '',
      icon: '',
      theme: '',

      showDialog: false,
    }),

    computed: {
      alertDialog() {
        return this.$store.state.dialog.alertDialog;
      },

      hideOverlay() {
        return !this.alertDialog?.showOverlay;
      },

      isDialogWhite() {
        return this.alertDialog.theme === 'white';
      },

      contentClass() {
        const res = ['fixed-right alert-dialog alert-shadow'];

        if (this.alertDialog.theme) {
          res.push(`dialog--${this.alertDialog.theme}`);
        }

        return res.join(' ');
      },

      isCtaEnabled() {
        return this.ctaText && typeof this.ctaAction === 'function';
      },

      ctaText() {
        return this.alertDialog?.ctaText;
      },

      ctaAction() {
        return this.alertDialog?.ctaAction;
      },

      doNotClose() {
        return !!this.alertDialog?.doNotClose;
      },
    },

    watch: {
      alertDialog() {
        this.showDialog = !!this.alertDialog;

        if (!this.alertDialog) {
          return;
        }

        const { text, title, subTitle, icon } = this.alertDialog;

        this.icon = icon;
        this.text = text;
        this.title = title;
        this.subTitle = subTitle;
      },
    },

    methods: {
      handleCtaAction() {
        this.$refs?.ctaButton?.$el?.setAttribute('disabled', 'true');
        this.ctaAction?.();
        this.closeDialog();
      },

      closeDialog() {
        if (this.doNotClose) return;
        this.$emit('close');
        this.$store.dispatch('hideAlertDialog', null);
      },
    },
  };
</script>

<template>
  <v-dialog
    v-if="showDialog"
    v-model="showDialog"
    :content-class="contentClass"
    :hide-overlay="hideOverlay"
    :persistent="doNotClose"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card class="d-flex flex-column justify-center align-center position-relative" min-height="190px">
      <template v-if="!doNotClose">
        <CloseIcon v-if="isDialogWhite" class="close-button" data-test-id="error-modal-close" @click="closeDialog" />
        <v-icon v-else color="white" data-test-id="error-modal-close" size="36" @click="closeDialog">mdi-close</v-icon>
      </template>

      <v-card-title v-if="title || subTitle" class="justify-center flex-column mt-5 alert-dialog-title">
        <img v-if="icon" :src="icon" alt="mail-icon" class="mb-3" width="120" />
        <span
          v-if="title"
          class="dialog-title white--text font-size-26 font-weight-bold wrapped-text"
          data-test-id="error-modal-title"
        >
          {{ title }}
        </span>
        <span
          v-if="subTitle"
          class="dialog-subtitle white--text font-size-26 font-weight-bold wrapped-text mt-2"
          data-test-id="error-modal-subtitle"
        >
          {{ subTitle }}
        </span>
      </v-card-title>
      <v-card-text
        class="dialog-text white--text text-center font-size-20 font-weight-semi"
        data-test-id="error-modal-text"
        v-html="text"
      />

      <YellowButton
        v-if="isCtaEnabled"
        ref="ctaButton"
        class="action-button"
        data-test-id="error-modal-cta-button"
        @click="handleCtaAction"
      >
        {{ ctaText }}
      </YellowButton>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
  .wrapped-text {
    overflow-wrap: break-word;
    white-space: normal;
    text-align: center;

    @media screen and (max-width: 400px) {
      font-size: 24px !important;
    }
  }

  .alert-dialog-title {
    @media screen and (max-width: 400px) {
      padding: 0 10px !important;
    }
  }

  .alert-dialog {
    box-shadow: 1px 1px 5px #000000 !important;

    .v-card {
      .v-icon {
        position: absolute;
        right: 15px;
        top: 15px;
      }

      span {
        word-break: break-word;
      }
    }
  }

  .dialog--white {
    .v-card {
      background: var(--Colors-Off-White, #f0f0f0) !important;
      gap: 24px;
      flex-direction: column;
    }

    .close-button {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 24px;
      height: 24px;
      cursor: pointer;

      &:hover {
        filter: brightness(105%);
      }

      &:active {
        filter: brightness(97%);
      }
    }

    .dialog-title {
      color: #1e1e1e !important;
      font-size: 32px;
      font-weight: 600;
      line-height: 26px;
      padding-top: 0;
      padding-bottom: 0;
    }

    .dialog-subtitle,
    .dialog-text {
      color: #1e1e1e !important;
      font-size: 18px;
      font-weight: 500;
      line-height: 26px;
      padding-top: 0;
      padding-bottom: 24px;
    }
  }

  .action-button {
    min-height: 58px;
    color: #1e1e1e;
    font-size: 16px;
    font-weight: 600;
    line-height: 26px;
  }
</style>

<!-- Warning. Global style -->
<style>
  @media screen and (max-width: 400px) {
    .v-dialog.alert-dialog {
      margin: 5px !important;
    }
  }

  .v-dialog.alert-dialog {
    border-radius: 25px;

    &.dialog--white {
      border-radius: 20px;
    }
  }
</style>
